import React, { useState, useMemo } from "react";
import { observer } from "mobx-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DateRangeIcon from "@mui/icons-material/DateRange";
import useStore from "../../helpers/useStore";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import {
  useClosestAppointmentsByWeek,
  useClosestContinuousAppointments,
} from "../../stores/Address/address.queries";
import moment from "moment";

/**
 * Formats a Euclidean distance to a human-readable string.
 * Uses a rough degree-to-km factor for German latitudes (~111km per degree).
 */
function formatDistance(euclidean: number): string {
  const km = euclidean * 111; // approximate for central Europe
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  return `${km.toFixed(1)} km`;
}

/**
 * Formats a driving travel time (in minutes) to a human-readable string.
 * Returns a dash when no travel time is available (address outside the closest set).
 */
function formatTravelTime(minutes?: number): string {
  if (minutes == null) {
    return "–";
  }
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remaining = Math.round(minutes % 60);
  return remaining ? `${hours} h ${remaining} min` : `${hours} h`;
}

/** German weekday name for a 0–6 day number (0 = Sunday, as EXTRACT(DOW) yields). */
const weekdayName = (dayOfWeek: number): string => moment().day(dayOfWeek).format("dddd");

type DateFilter = "week" | "day";

/**
 * "Nächstgelegene Termine" — what else is being treated near this address, so a
 * home visit can be slotted into an existing route rather than starting one.
 *
 * The modal serves BOTH calendar planes and never mixes them: in the ordinary
 * calendar it lists dated appointments in the viewed week; in Dauertermin view
 * it lists Dauertermine by weekday. Which plane (and which target) it searches
 * comes from `CalendarStore.closestAppointmentsTarget`, set by the shelf surface
 * that opened it — a card knows its own id and plane, the modal would have to
 * guess. In both planes the filter toggle is the same idea: everything, or only
 * the day currently on screen.
 */
const ClosestAppointmentsModal: React.FC = observer(() => {
  const CalendarStore = useStore(CalendarContext);
  const { closestAppointmentsModalOpen, closestAppointmentsTarget, selectedDateStr } =
    CalendarStore;

  const [dateFilter, setDateFilter] = useState<DateFilter>("week");

  const isContinuous = closestAppointmentsTarget?.scope === "continuous";
  const hasTarget = !!(
    closestAppointmentsTarget?.appointmentId ||
    closestAppointmentsTarget?.continuousAppointmentId ||
    closestAppointmentsTarget?.addressId
  );

  // Compute Monday of the current week from selectedDateStr
  const weekStartDate = moment(selectedDateStr).startOf("isoWeek").format("YYYY-MM-DD");
  const selectedDay = moment(selectedDateStr).format("YYYY-MM-DD");
  // The weekday the continuous calendar is showing — the counterpart of
  // `selectedDay` on the plane that has no dates.
  const selectedDayOfWeek = moment(selectedDateStr).day();

  const { data: closestAppointments, isLoading: isLoadingDated } = useClosestAppointmentsByWeek(
    closestAppointmentsTarget?.appointmentId,
    closestAppointmentsTarget?.addressId,
    weekStartDate,
    selectedDay,
    closestAppointmentsModalOpen && !isContinuous && hasTarget,
  );

  const { data: closestContinuous, isLoading: isLoadingContinuous } =
    useClosestContinuousAppointments(
      closestAppointmentsTarget?.continuousAppointmentId,
      closestAppointmentsTarget?.addressId,
      selectedDayOfWeek,
      closestAppointmentsModalOpen && isContinuous && hasTarget,
    );

  const isLoading = isContinuous ? isLoadingContinuous : isLoadingDated;

  /**
   * One row shape for both planes, so the table below stays a single render.
   * `dayLabel` is a date in one plane and a weekday in the other; `jumpDate` is
   * where "zum Kalender" navigates — for a Dauertermin that is its weekday
   * inside the week already on screen, since it has no date of its own.
   */
  const rows = useMemo(() => {
    if (isContinuous) {
      const source = closestContinuous ?? [];
      const filtered =
        dateFilter === "day"
          ? source.filter((appt) => appt.dayOfWeek === selectedDayOfWeek)
          : source;
      return filtered.map((appt) => ({
        key: `${appt.continuousAppointmentId}-${appt.dayOfWeek}`,
        appointmentId: appt.continuousAppointmentId,
        dayLabel: weekdayName(appt.dayOfWeek),
        jumpDate: moment(selectedDateStr).day(appt.dayOfWeek).format("YYYY-MM-DD"),
        start: appt.start,
        end: appt.end,
        patientName: appt.patientName,
        therapistName: appt.therapistName,
        addressString: appt.addressString,
        distance: appt.distance,
        travelTimeMinutes: appt.travelTimeMinutes,
      }));
    }
    const source = closestAppointments ?? [];
    const filtered =
      dateFilter === "day" ? source.filter((appt) => appt.date === selectedDay) : source;
    return filtered.map((appt) => ({
      key: appt.appointmentId,
      appointmentId: appt.appointmentId,
      dayLabel: moment(appt.date).format("dd, DD.MM."),
      jumpDate: appt.date,
      start: appt.start,
      end: appt.end,
      patientName: appt.patientName,
      therapistName: appt.therapistName,
      addressString: appt.addressString,
      distance: appt.distance,
      travelTimeMinutes: appt.travelTimeMinutes,
    }));
  }, [
    isContinuous,
    closestContinuous,
    closestAppointments,
    dateFilter,
    selectedDay,
    selectedDayOfWeek,
    selectedDateStr,
  ]);

  const handleClose = () => {
    CalendarStore.setClosestAppointmentsModalOpen(false);
  };

  const handleJumpToAppointment = (appointmentId: string, date: string) => {
    // Navigate to the date and highlight the appointment without changing patient context.
    // The view itself is not switched: a continuous row is only ever reachable
    // from the continuous calendar, and a dated one from the dated calendar.
    handleClose();
    CalendarStore.peekMode = true;
    CalendarStore.setSelectedDateStr(date);
    CalendarStore.setTargetAppointmentId(appointmentId);
  };

  return (
    <Dialog open={closestAppointmentsModalOpen} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{isContinuous ? "Nächstgelegene Dauertermine" : "Nächstgelegene Termine"}</span>
        <ToggleButtonGroup
          value={dateFilter}
          exclusive
          onChange={(_, val) => val && setDateFilter(val)}
          size="small"
        >
          <ToggleButton value="week" sx={{ gap: 0.5, textTransform: "none" }}>
            <DateRangeIcon fontSize="small" />
            {isContinuous
              ? "Alle Tage"
              : `${moment(weekStartDate).format("DD.MM.")} – ${moment(weekStartDate)
                  .add(6, "days")
                  .format("DD.MM.")}`}
          </ToggleButton>
          <ToggleButton value="day" sx={{ gap: 0.5, textTransform: "none" }}>
            <CalendarTodayIcon fontSize="small" />
            {isContinuous
              ? weekdayName(selectedDayOfWeek)
              : moment(selectedDateStr).format("dd, DD.MM.")}
          </ToggleButton>
        </ToggleButtonGroup>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : !hasTarget ? (
          <Typography color="text.secondary" p={2}>
            Für den ausgewählten Termin fehlt eine Referenz.
          </Typography>
        ) : !rows.length ? (
          <Typography color="text.secondary" p={2}>
            {isContinuous
              ? dateFilter === "day"
                ? "Keine Dauertermine mit Koordinaten an diesem Wochentag gefunden."
                : "Keine Dauertermine mit Koordinaten gefunden."
              : `Keine Termine mit Koordinaten ${
                  dateFilter === "day" ? "an diesem Tag" : "in dieser Woche"
                } gefunden.`}
          </Typography>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Fahrzeit</TableCell>
                <TableCell>Entfernung</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Zeit</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Therapeut</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell align="center">Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={row.travelTimeMinutes != null ? "primary" : "text.secondary"}
                    >
                      {formatTravelTime(row.travelTimeMinutes)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDistance(row.distance!)}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.dayLabel}</TableCell>
                  <TableCell>
                    {moment(row.start).format("HH:mm")} – {moment(row.end).format("HH:mm")}
                  </TableCell>
                  <TableCell>{row.patientName}</TableCell>
                  <TableCell>{row.therapistName}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {row.addressString}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Zum Kalender">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleJumpToAppointment(row.appointmentId!, row.jumpDate!)}
                      >
                        <CalendarTodayIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
});

ClosestAppointmentsModal.displayName = "ClosestAppointmentsModal";
export default ClosestAppointmentsModal;
